import { apiClient } from './client';

// チャット: device へ prompt を投げ、claude の結果を同期で受け取る。
// claude 実行はサーバ側で最大 180s 待つので、axios 側もタイムアウトなし（既定）。
// ADR0008: スレッド = conversationId（会話開始時に発番、最初から有る）。
// session_id は CLI 継続トークン（resume 用）で別物。
export interface ChatResponse {
  delivered: boolean;
  reason?: string;
  // AI-WF251 (streaming): POST は即 ack。'running'=stream 開始。本文は SSE(chunk→final)で届く。
  status?: 'ok' | 'error' | 'timeout' | 'running';
  output?: string;
  durationMs?: number;
  // 会話=スレッドID。新スレッドは Backend が発番して返す。次メッセージでこれを送ると同じ会話に束ねる。
  conversationId?: string;
  // claude の session_id（次メッセージで resumeSessionId として送ると CLI 会話継続）。
  sessionId?: string;
  // AI-WF251: 進行中 AI メッセージの messageId と run の requestId（cancel に使う）。
  messageId?: string;
  requestId?: string;
}

// AI-WF251 (streaming・B6): 進行中 run(requestId) を中断する。
export interface CancelChatResponse {
  delivered: boolean;
  reason?: string;
}
export async function cancelChat(
  deviceId: number,
  requestId: string,
): Promise<CancelChatResponse> {
  const res = await apiClient.post<CancelChatResponse>(
    `/api/admin/devices/${deviceId}/chat/cancel`,
    { requestId },
  );
  return res.data;
}

// 添付画像（アップロード済み）の参照。チャット欄表示にも使う。
export interface ChatAttachment {
  fileId: string;
  name: string;
  mime: string;
  size?: number;
  // 配信URL（/api/admin/chat-files/:fileId）。表示は認証付き blob 取得（getChatFileBlob）で行う。
  url: string;
}

export async function consoleChat(
  deviceId: number,
  body: {
    projectId: number;
    cwd: string;
    prompt: string;
    resumeSessionId?: string;
    conversationId?: string;
    teamId?: number;
    // @宛先（任意）。未指定はチームのPM(リーダー)既定。agent:<id> / controller。
    toRef?: string;
    // ブロードキャスト束ね（任意）。一斉送信時に対象チーム分へ同じ値を載せる（bc_<uuid>）。
    broadcastId?: string;
    // 添付画像（事前に uploadChatFiles で得た fileId 群）。
    attachments?: { fileId: string; name: string; mime: string }[];
    // ▼ /コマンド（境界契約 §3.1）。FE は `/name` を name(先頭/抜き) と args に分けて送るだけ。
    // 種別(kind)解決・ルーティングは Backend 側で行う（FE は kind を判断しない）。
    commandName?: string;
    commandArgs?: string;
    // ▼ AI-WF262 (要望#211/TA-5): true=非同期モード（受領→進捗→最終の複数メッセージ＋完了push通知）。
    // 省略/false は従来同期（見た目不変・#204 後方互換）。
    async?: boolean;
  },
): Promise<ChatResponse> {
  const res = await apiClient.post<ChatResponse>(
    `/api/admin/devices/${deviceId}/chat`,
    body,
  );
  return res.data;
}

// スレッド = conversationId（ADR0008）。sessionId は CLI 継続トークン（resume 用）。
export interface ThreadSummary {
  conversationId: string;
  sessionId: string | null;
  title: string;
  lastText: string;
  lastAt: string;
  count: number;
  // 未読件数（サーバ管理。このユーザーの既読位置より新しいメッセージ数）。
  unread: number;
  deviceId: number | null;
  teamId: number | null;
  // ソフトアーカイブ済みか（通常一覧から隠し、アーカイブ欄に出す）。
  archived: boolean;
  // 完了・中止済みか（通常一覧から隠し、それぞれのセクションに出す）。
  completed: boolean;
  cancelled: boolean;
  // お気に入り登録済みか（星マーク ON/OFF）。チームをまたいだお気に入り一覧に出す。
  favorited: boolean;
}
// コマンド/実行の結果（境界契約 §3.2）。Backend→FE。
// kind は Backend が解決した種別、status はアクションの進捗、output は結果/エラー。
export interface ThreadAction {
  kind: 'command' | 'exec' | 'skill' | 'session-control' | 'file';
  name: string; // 例 'ls' / 'deploy' / 'clear'
  status: 'pending' | 'running' | 'ok' | 'error';
  output?: string; // 結果/エラー（exec の stdout/stderr 等）
  startedAt?: string;
  completedAt?: string;
}

export interface ThreadMessage {
  id: number;
  role: 'user' | 'ai' | 'system';
  text: string;
  createdAt: string;
  deviceId: number | null;
  // 誰が発言したか（AI応答の解決済み話者）。例: 'agent:12' / 'controller' / null。
  fromRef?: string | null;
  // 添付画像（file アクション）。送信したメッセージにはここに入って返る。
  attachments?: ChatAttachment[];
  // ▼ 会話/メッセージの状態（境界契約 §3.2）。返信待ち の起点＝Worker の needsReply（質問）。
  status?: 'idle' | '対応中' | '返信待ち' | 'ok' | 'error' | 'timeout' | 'cancelled';
  // ▼ コマンド/実行の結果（境界契約 §3.2）。SSE update で in-place パッチされる。
  actions?: ThreadAction[];
  // ▼ AI-WF251 (streaming)。lifecycle・終端理由・適用済み seq（resume/返信待ち復元の source）。
  streamState?: 'pending' | 'streaming' | 'final';
  stopReason?: string | null;
  lastSeq?: number;
  // ▼ AI-WF262 (要望#211/TA-5): 非同期会話の種別（受領/作業中/完了チップ表示に使う）。
  // normal=従来 / ack=受領 / progress=進捗 / final=非同期の最終（#204 が刻む）。
  msgKind?: 'normal' | 'ack' | 'progress' | 'final';
  // ▼ タスク#408: run の識別子。各メッセージに `sessionId-requestId` を表示し、
  // requestId でメッセージ単位（最新以外も）の cancel ができる。旧行・run 外の行は null。
  sessionId?: string | null;
  requestId?: string | null;
  // ▼ task#450: probe OK バブルフラグ。true のメッセージはトグルで非表示にできる。
  isProbe?: boolean;
  // ▼ クライアント専用（サーバは返さない）: 進行中の作業内容ログ（鼓動）。
  // SSE の progress chunk が届くたび「時刻つきで追記」していく（上書きしない）。
  // 「今この依頼が動いているか」＋「どこで止まったか」を操作卓で見せる debug 用。
  // 多すぎる場合は将来 overwrite 方式へ戻す余地あり（社長メモ 2026-08）。
  liveActivity?: LiveActivityEntry[] | null;
}

// 鼓動ログ1行（t=時刻 HH:MM:SS / label=丸めた作業内容 / ms=生成時の epoch ms・経過時間計算用）。
export interface LiveActivityEntry {
  t: string;
  label: string;
  ms?: number;
}

// 添付画像をアップロード（複数可）。multipart/form-data・field 名は "files"。
export async function uploadChatFiles(files: File[]): Promise<ChatAttachment[]> {
  const fd = new FormData();
  for (const f of files) fd.append('files', f, f.name);
  const res = await apiClient.post<ChatAttachment[]>('/api/admin/chat-files/upload', fd);
  return res.data;
}

// 添付画像の実体を認証付きで取得して blob を返す（<img> 直 src は Bearer を送れないため）。
export async function getChatFileBlob(fileId: string): Promise<Blob> {
  const res = await apiClient.get(`/api/admin/chat-files/${encodeURIComponent(fileId)}`, {
    responseType: 'blob',
  });
  return res.data as Blob;
}

export async function listProjectThreads(projectId: number): Promise<ThreadSummary[]> {
  const res = await apiClient.get<ThreadSummary[]>(`/api/admin/projects/${projectId}/threads`);
  return res.data;
}

// ブロードキャスト（仮想チーム）。専用レコードは無く、実体スレを broadcastId で束ねたもの。
// 1グループ = 1回の一斉送信。配下に「チームごとの実体スレ」を持つ（返信は各実体スレで完結）。
export interface BroadcastThread {
  conversationId: string;
  // 配下スレの所属PJ（全プロジェクト・ブロードキャストでPJをまたぐ束ね表示に使う）。
  projectId: number;
  projectName: string;
  teamId: number | null;
  teamName: string;
  teamIcon: string | null;
  title: string;
  lastText: string;
  lastAt: string;
  count: number;
  unread: number;
}
export interface BroadcastSummary {
  broadcastId: string;
  text: string; // 送信した文面（配下スレの最初のユーザー発話）
  createdAt: string;
  lastAt: string;
  teamCount: number;
  unread: number;
  threads: BroadcastThread[];
}
export async function listProjectBroadcasts(projectId: number): Promise<BroadcastSummary[]> {
  const res = await apiClient.get<BroadcastSummary[]>(
    `/api/admin/projects/${projectId}/broadcasts`,
  );
  return res.data;
}

// 全プロジェクト・ブロードキャスト（テナント横断）。PJをまたぐ broadcastId 束ねを返す。
export async function listAllBroadcasts(): Promise<BroadcastSummary[]> {
  const res = await apiClient.get<BroadcastSummary[]>('/api/admin/broadcasts');
  return res.data;
}

// スレッド（会話）を既読にする（サーバの既読状態を更新）。
export async function markThreadRead(conversationId: string): Promise<void> {
  await apiClient.post(`/api/admin/threads/${encodeURIComponent(conversationId)}/read`);
}

// スレッドのタイトルを変更する（ダブルクリック編集）。
export async function renameThread(conversationId: string, title: string): Promise<void> {
  await apiClient.patch(`/api/admin/threads/${encodeURIComponent(conversationId)}/title`, { title });
}

// スレッドをアーカイブ（ソフト削除・物理削除しない） / 復活する。
export async function archiveThread(conversationId: string): Promise<void> {
  await apiClient.post(`/api/admin/threads/${encodeURIComponent(conversationId)}/archive`);
}
export async function unarchiveThread(conversationId: string): Promise<void> {
  await apiClient.post(`/api/admin/threads/${encodeURIComponent(conversationId)}/unarchive`);
}

// スレッドを完了・中止としてクローズ / 復活する。
export async function completeThread(conversationId: string): Promise<void> {
  await apiClient.post(`/api/admin/threads/${encodeURIComponent(conversationId)}/complete`);
}
export async function uncompleteThread(conversationId: string): Promise<void> {
  await apiClient.post(`/api/admin/threads/${encodeURIComponent(conversationId)}/uncomplete`);
}
export async function cancelThread(conversationId: string): Promise<void> {
  await apiClient.post(`/api/admin/threads/${encodeURIComponent(conversationId)}/cancel`);
}
export async function uncancelThread(conversationId: string): Promise<void> {
  await apiClient.post(`/api/admin/threads/${encodeURIComponent(conversationId)}/uncancel`);
}

// スレッドを完全削除（物理削除）。アーカイブ欄からのみ呼ぶ二段構え。
export async function deleteThread(conversationId: string): Promise<void> {
  await apiClient.delete(`/api/admin/threads/${encodeURIComponent(conversationId)}`);
}

// スレッドのお気に入り登録（星マーク ON）/ 解除（星マーク OFF）。
export async function favoriteThread(conversationId: string): Promise<void> {
  await apiClient.post(`/api/admin/threads/${encodeURIComponent(conversationId)}/favorite`);
}
export async function unfavoriteThread(conversationId: string): Promise<void> {
  await apiClient.post(`/api/admin/threads/${encodeURIComponent(conversationId)}/unfavorite`);
}

// 全件チャット検索（テナント横断）。ヘッダーの検索UIから呼ぶ。
export interface ChatSearchResult {
  conversationId: string;
  projectId: number;
  projectName: string;
  teamId: number | null;
  teamName: string | null;
  teamIcon: string | null;
  role: 'user' | 'ai' | 'system';
  snippet: string;
  createdAt: string;
}
export async function searchChat(q: string): Promise<ChatSearchResult[]> {
  const res = await apiClient.get<ChatSearchResult[]>('/api/admin/chat/search', {
    params: { q },
  });
  return res.data;
}

export async function getThreadMessages(conversationId: string): Promise<ThreadMessage[]> {
  const res = await apiClient.get<ThreadMessage[]>(
    `/api/admin/threads/${encodeURIComponent(conversationId)}/messages`,
  );
  return res.data;
}

// ── リアルタイム配信(SSE 1対N)。設計doc 10 ──
// 受信は SSE 片方向 push。送信は従来どおり POST(consoleChat)据置。
// EventSource は Authorization ヘッダを送れないため JWT を ?token= で渡す。
// 配信ペイロード（境界契約 §3.2 と同型）:
export interface ThreadStreamMessageEvent {
  kind: 'message';
  conversationId: string;
  message: ThreadMessage; // 追加された1メッセージ（getThreadMessages の1要素と同型）
}
export interface ThreadStreamUpdateEvent {
  kind: 'update';
  conversationId: string;
  messageId: string; // 更新対象（chat_messages.message_id・相関用）
  id: number; // ThreadMessage.id（in-place パッチ用の数値 id）
  status?: ThreadMessage['status'] | 'idle' | 'ok' | 'error' | 'timeout' | null;
  actions?: ThreadAction[];
  // AI-WF251 (streaming): final 確定時の lifecycle・終端理由・resume 用 session_id。
  streamState?: 'pending' | 'streaming' | 'final';
  stopReason?: string | null;
  sessionId?: string | null;
}
// AI-WF251 (streaming): 進行中 AI メッセージへの text 増分。id で対象バブルを引き
// seq>lastSeq の delta だけ append する（冪等・順序保証・C3）。
export interface ThreadStreamChunkEvent {
  kind: 'chunk';
  conversationId: string;
  messageId: string;
  id: number;
  seq: number;
  delta: string;
  chunkKind: 'text' | 'progress';
}
// task#450: probe→実run 切替時に FE の lastSeq を 0 にリセットする。
export interface ThreadStreamSeqResetEvent {
  kind: 'seq-reset';
  conversationId: string;
  id: number;
}

export type ThreadStreamEvent =
  | ThreadStreamMessageEvent
  | ThreadStreamUpdateEvent
  | ThreadStreamChunkEvent
  | ThreadStreamSeqResetEvent;

// SSE 接続用の URL を組み立てる（baseURL は apiClient と同じ env を参照）。
export function threadStreamUrl(conversationId: string, accessToken: string): string {
  const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:23015';
  const path = `/api/admin/threads/${encodeURIComponent(conversationId)}/stream`;
  const qs = `?token=${encodeURIComponent(accessToken)}`;
  return `${baseURL}${path}${qs}`;
}
